import { useState } from 'react';
import { Select, Button, Divider, Space, Typography, Empty, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';

const { Text } = Typography;

const sectionHeaderStyle = {
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#6b7280',
    background: '#f9fafb',
};

export default function ParamMotifSelect({ type, value, onChange, placeholder, disabled }) {
    const { t } = useTranslation('commun');
    const queryClient = useQueryClient();
    const [searchText, setSearchText] = useState('');

    const { data: motifs = [], isLoading } = useQuery({
        queryKey: ['param-motifs', type],
        enabled: !!type,
        queryFn: async () => {
            const { data } = await axiosInstance.get('/api/v1/param-motifs', { params: { type } });
            // Le backend renvoie soit un tableau direct (List<ParamMotifResponse>),
            // soit { data: [...] } (DataResponse), soit { content: [...] } (PaginatedResponse).
            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.data)) return data.data;
            if (Array.isArray(data?.content)) return data.content;
            return [];
        },
    });

    const creer = useMutation({
        mutationFn: async (libelle) => {
            const cleaned = (libelle || '').trim();
            if (!cleaned || cleaned.length < 3) {
                throw new Error(t('paramMotifSelect.libelleTropCourt'));
            }
            if (!type) {
                throw new Error(t('paramMotifSelect.typeManquant'));
            }
            const { data } = await axiosInstance.post('/api/v1/param-motifs', {
                libelle: cleaned, type, actif: true,
            });
            // Robuste : le POST peut renvoyer { data: {...} } (DataResponse) ou {...} direct.
            return data?.data ?? data;
        },
        onSuccess: (created) => {
            const libelle = created?.libelle ?? created?.libelleMotif;
            if (libelle) {
                message.success(t('paramMotifSelect.motifCree', { libelle }));
                onChange(libelle);
            }
            queryClient.invalidateQueries({ queryKey: ['param-motifs', type] });
            setSearchText('');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || t('paramMotifSelect.erreurCreation'));
        },
    });

    const matchExact = motifs.some(
        (m) => m.libelle.toLowerCase().trim() === searchText.toLowerCase().trim()
    );
    const peutAfficherCreer = searchText.trim().length >= 3 && !matchExact;

    const handleCreer = () => {
        const libelle = searchText.trim();
        if (!libelle || !type) return;
        creer.mutate(libelle);
    };

    return (
        <Select
            showSearch
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            optionFilterProp="label"
            filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onSearch={setSearchText}
            options={motifs.map((m) => ({ value: m.libelle, label: m.libelle }))}
            style={{ width: '100%', borderRadius: 8 }}
            notFoundContent={
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<Text type="secondary">{t('paramMotifSelect.aucunMotif')}</Text>}
                    style={{ margin: '8px 0' }}
                />
            }
            dropdownRender={(menu) => (
                <div>
                    {/* Section 1 - Motifs enregistrés */}
                    <div style={sectionHeaderStyle}>{t('paramMotifSelect.motifsEnregistres')}</div>
                    {menu}

                    <Divider style={{ margin: '4px 0' }} />

                    {/* Section 2 - Ajouter un motif */}
                    <div style={sectionHeaderStyle}>{t('paramMotifSelect.ajouterMotif')}</div>
                    <Space style={{ padding: '6px 12px', display: 'flex' }}>
                        {peutAfficherCreer ? (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                loading={creer.isPending}
                                onClick={handleCreer}
                            >
                                {t('paramMotifSelect.creer', { libelle: searchText.trim() })}
                            </Button>
                        ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {searchText.trim().length === 0
                                    ? t('paramMotifSelect.tapezLibelle')
                                    : searchText.trim().length < 3
                                    ? t('paramMotifSelect.encoreCaracteres', { nombre: 3 - searchText.trim().length })
                                    : t('paramMotifSelect.libelleExiste')}
                            </Text>
                        )}
                    </Space>
                </div>
            )}
        />
    );
}
